import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import TextInput from "../../components/UI/TextInput";
import ToggleableButton from "../../components/UI/ToggleagleButton";
import Table from "../../components/UI/Table";
import { httpClient } from "../../../infrastructure/api/http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import { resolveErrorMessage } from "../../../shared/utils/save-flow";
import {
  CASH_RECEIPT_USAGE_LABELS,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  NTS_STATE_LABELS,
  type InvoiceAdjustmentSummary,
  type InvoiceItem,
  type TaxDocumentConnection,
} from "../../../domain/types/tax-document";
import { Badge, InvoiceDetailDialog } from "../../components/CompanyAdmin/TaxDocument/InvoiceDetailDialog";
import { InvoiceCreateDialog } from "../../components/CompanyAdmin/TaxDocument/InvoiceCreateDialog";
import { TaxDocumentConnectionBanner } from "../../components/CompanyAdmin/TaxDocument/TaxDocumentConnectionBanner";
import { openUrlFromServer, taxDocumentApi } from "../../components/CompanyAdmin/TaxDocument/tax-document-api";

interface PageResponse<T> {
  items: T[];
  totalPages: number;
  totalElements: number;
  pageSize: number;
  next: string | null;
  previous: string | null;
}

const PAGE_SIZE = 50;

/** 발행여부 칩 중 "처리 필요" — status 대신 needsAction=true 로 조회한다 */
const NEEDS_ACTION = "NEEDS_ACTION";

/** 발급 버튼을 눌러도 되는 행: 결제 확인 + (미발행/실패/국세청 전송 실패) */
const isIssuable = (r: InvoiceItem) =>
  r.orderPaid && (r.status === "PENDING" || r.status === "FAIL" || (r.status === "SUCCESS" && r.ntsState === "SEND_FAILED"));

/**
 * 세금계산서 / 현금영수증 관리.
 *
 * 쇼핑몰 주문서·주문 상세에서 고객이 신청한 건, 담당자가 주문번호로 등록한 건이 모인다.
 * 홈택스 발급 연동(내 회사 정보 > 추가 서비스 연동)이 되어 있으면 여기서 국세청으로 바로 발급한다.
 * 연동 전에는 예전처럼 직접 발급한 뒤 "수기 발행 처리"로 장부만 맞출 수 있다.
 */
const TaxReceipt: React.FC = () => {
  const { companyId } = useParams();
  const cid = Number(companyId);

  const [records, setRecords] = useState<InvoiceItem[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [detailRecord, setDetailRecord] = useState<InvoiceItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [connection, setConnection] = useState<TaxDocumentConnection | null>(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [rowBusyId, setRowBusyId] = useState<number | null>(null);
  const [adjustmentSummary, setAdjustmentSummary] = useState<InvoiceAdjustmentSummary | null>(null);

  const loadAdjustmentSummary = useCallback(async () => {
    if (!Number.isFinite(cid)) return;
    try {
      setAdjustmentSummary(await taxDocumentApi.adjustmentSummary(cid));
    } catch (error) {
      console.error("환불 조정 요약 조회 실패:", error);
    }
  }, [cid]);

  const loadConnection = useCallback(async () => {
    if (!Number.isFinite(cid)) return;
    try {
      setConnection(await taxDocumentApi.connection(cid));
    } catch (error) {
      console.error("발급 연동 상태 조회 실패:", error);
    }
  }, [cid]);

  const loadRecords = useCallback(async () => {
    if (!Number.isFinite(cid)) return;
    setLoading(true);
    try {
      const params: Record<string, any> = { page, size: PAGE_SIZE };
      if (searchKeyword) params.keyword = searchKeyword;
      if (typeFilter !== "ALL") params.invoiceType = typeFilter;
      if (statusFilter === NEEDS_ACTION) params.needsAction = true;
      else if (statusFilter !== "ALL") params.status = statusFilter;

      const response = await httpClient.get<{ data: PageResponse<InvoiceItem> }>(
        NEW_API_ENDPOINTS.companyInvoiceController.GET(cid),
        { params },
      );
      if (response.data.data) {
        setRecords(response.data.data.items);
        setTotalElements(response.data.data.totalElements);
        setTotalPages(response.data.data.totalPages);
      }
    } catch (error) {
      console.error("세금계산서 목록 조회 실패:", error);
    } finally {
      setLoading(false);
    }
  }, [cid, page, searchKeyword, typeFilter, statusFilter]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  useEffect(() => {
    loadAdjustmentSummary();
  }, [loadAdjustmentSummary]);

  // 필터가 바뀌면 첫 페이지로, 선택은 비운다
  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
  }, [searchKeyword, typeFilter, statusFilter]);

  const connected = connection?.connected === true;

  const replaceRecord = (next: InvoiceItem) => {
    setRecords((prev) => prev.map((r) => (r.id === next.id ? next : r)));
    setDetailRecord((prev) => (prev && prev.id === next.id ? next : prev));
    // 상세에서 조정 건을 처리하면 처리 대기 건수가 바뀐다
    loadAdjustmentSummary();
  };

  const handleBatchIssue = async () => {
    if (selectedIds.size === 0) {
      alert("발급할 항목을 선택해주세요.");
      return;
    }
    if (!window.confirm(`${selectedIds.size}건을 국세청으로 일괄 발급하시겠습니까?\n발급 후에는 내용을 고칠 수 없습니다.`)) return;
    setBatchBusy(true);
    try {
      const results = await taxDocumentApi.batchIssue(cid, Array.from(selectedIds));
      const failed = results.filter((r) => !r.success);
      const okCount = results.length - failed.length;
      const lines = failed.slice(0, 10).map((f) => {
        const row = records.find((r) => r.id === f.id);
        return `· ${row?.orderNumber ?? f.id}: ${f.message ?? "실패"}`;
      });
      alert(
        failed.length === 0
          ? `${okCount}건을 발급했습니다.`
          : `${okCount}건 발급, ${failed.length}건 실패\n\n${lines.join("\n")}${failed.length > 10 ? "\n…" : ""}`,
      );
      setSelectedIds(new Set());
      loadRecords();
    } catch (error) {
      alert(resolveErrorMessage(error, "일괄 발급에 실패했습니다."));
    } finally {
      setBatchBusy(false);
    }
  };

  const handleRowIssue = async (record: InvoiceItem) => {
    const label = INVOICE_TYPE_LABELS[record.invoiceType];
    if (!window.confirm(`${record.orderNumber ?? ""} ${label}를 국세청으로 발급하시겠습니까?`)) return;
    setRowBusyId(record.id);
    try {
      replaceRecord(await taxDocumentApi.issue(cid, record.id));
    } catch (error) {
      alert(resolveErrorMessage(error, "발급에 실패했습니다."));
      loadRecords();
    } finally {
      setRowBusyId(null);
    }
  };

  const handleRowView = async (record: InvoiceItem) => {
    try {
      await openUrlFromServer(() => taxDocumentApi.viewUrl(cid, record.id));
    } catch (error) {
      alert(resolveErrorMessage(error, "증빙 화면을 열 수 없습니다."));
    }
  };

  const selectable = records.filter((r) => connected && isIssuable(r));

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === selectable.length && selectable.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectable.map((r) => r.id)));
    }
  };

  const handleSearch = () => setSearchKeyword(searchInput);
  const handleReset = () => { setSearchInput(""); setSearchKeyword(""); };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("ko-KR", {
      year: "numeric", month: "2-digit", day: "2-digit",
    });
  };

  const tabClass = (tab: string, current: string) =>
    `px-4 py-2 text-[13px] font-medium cursor-pointer border-b-2 ${
      current === tab
        ? "border-[#4285F4] text-[#4285F4]"
        : "border-transparent text-[#888] hover:text-[#555]"
    }`;

  const buyerSummary = (r: InvoiceItem) =>
    r.invoiceType === "TAX_INVOICE"
      ? r.buyerBusinessName ?? "-"
      : `${r.cashReceiptUsage ? CASH_RECEIPT_USAGE_LABELS[r.cashReceiptUsage] : ""} ${r.identityNum ?? ""}`.trim() || "-";

  return (
    <CompanyAdminLayout>
      <div className="p-5">
        {/* Header */}
        <div className="mb-[28px] flex items-center justify-between">
          <div className="flex flex-col items-start gap-[0.5rem]">
            <h2 className="text-[1.375rem] font-semibold">세금계산서/현금영수증 관리</h2>
            <span className="text-[1rem] font-normal">총 {totalElements}건</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCreating(true)}
              className="px-4 py-2 bg-white text-[#222] border border-[#222] text-[13px] rounded-[6px] hover:bg-[#f5f5f5]"
            >
              증빙 등록
            </button>
            <button
              onClick={handleBatchIssue}
              disabled={selectedIds.size === 0 || batchBusy || !connected}
              className="px-4 py-2 bg-[#222] text-white text-[13px] rounded-[6px] hover:bg-[#333] disabled:bg-[#ccc]"
            >
              {batchBusy ? "발급 중..." : `선택 일괄 발급 (${selectedIds.size})`}
            </button>
          </div>
        </div>

        <TaxDocumentConnectionBanner companyId={cid} connection={connection} onReload={loadConnection} />

        {adjustmentSummary && adjustmentSummary.pendingCount > 0 && (
          <div className="mb-5 flex items-center justify-between gap-3 rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-3 text-[13px] text-[#4285F4]">
            <span>
              환불로 처리할 증빙이 {adjustmentSummary.pendingCount}건 있습니다
              {adjustmentSummary.dueSoonCount > 0 &&
                ` · 기한 임박 ${adjustmentSummary.dueSoonCount}건 (가장 빠른 기한 ${adjustmentSummary.nearestDueDate ?? "-"})`}
            </span>
            {statusFilter !== NEEDS_ACTION && (
              <button
                onClick={() => setStatusFilter(NEEDS_ACTION)}
                className="shrink-0 px-3 py-1.5 text-[12px] rounded-[6px] bg-[#222] text-white hover:bg-[#333]"
              >
                처리 필요 건 보기
              </button>
            )}
          </div>
        )}

        {/* 증빙구분 필터 */}
        <div className="flex gap-0 border-b border-[#eee] mb-5">
          <div className={tabClass("ALL", typeFilter)} onClick={() => setTypeFilter("ALL")}>전체</div>
          <div className={tabClass("TAX_INVOICE", typeFilter)} onClick={() => setTypeFilter("TAX_INVOICE")}>세금계산서</div>
          <div className={tabClass("CASH_RECEIPT", typeFilter)} onClick={() => setTypeFilter("CASH_RECEIPT")}>현금영수증</div>
        </div>

        {/* 발행여부 필터 + 검색 */}
        <div className="mb-5 p-5 border border-[#d9d9d9] rounded-[10px] bg-white flex flex-col md:flex-row gap-3">
          <div className="flex gap-2 flex-wrap">
            {[
              { key: "ALL", label: "전체" },
              { key: "PENDING", label: "미발행" },
              { key: "SUCCESS", label: "발행완료" },
              { key: "FAIL", label: "실패" },
              { key: "CANCELED", label: "취소" },
              { key: NEEDS_ACTION, label: "처리 필요" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 text-[12px] rounded-[6px] ${
                  statusFilter === f.key
                    ? "bg-[#222] text-white"
                    : "bg-white text-[#222] border border-[#222] hover:bg-[#f5f5f5]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <TextInput
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="flex-1"
            placeholder="주문자명, 주문번호, 주문명, 상호, 승인번호 검색"
          />
          <ToggleableButton isEnabled={true} className="min-w-[85px] h-12" onClick={handleSearch}>
            검색
          </ToggleableButton>
          <ToggleableButton isEnabled={searchInput !== ""} className="min-w-[85px] h-12" onClick={handleReset}>
            초기화
          </ToggleableButton>
        </div>

        {/* 테이블 */}
        <div className="text-[12px]">
          {loading ? (
            <div className="py-10 text-center text-[14px] text-[#999]">불러오는 중...</div>
          ) : records.length === 0 ? (
            <div className="py-10 text-center text-[14px] text-[#bbb]">
              {searchKeyword || typeFilter !== "ALL" || statusFilter !== "ALL"
                ? "검색 결과가 없습니다."
                : "세금계산서/현금영수증 대상 주문이 없습니다."}
            </div>
          ) : (
            <Table
              columnWidths={["36px", "165px", "75px", "180px", "95px", "95px", "150px", "90px", "120px", "auto"]}
              tabledata={{
                head: [
                  <input
                    type="checkbox"
                    checked={selectable.length > 0 && selectedIds.size === selectable.length}
                    onChange={toggleSelectAll}
                    disabled={selectable.length === 0}
                    className="w-4 h-4"
                  />,
                  <>주문번호</>,
                  <>주문자</>,
                  <>주문명</>,
                  <>금액</>,
                  <>증빙구분</>,
                  <>받는 분</>,
                  <>발행상태</>,
                  <>국세청</>,
                  <>관리</>,
                ],
                body: records.map((record) => [
                  <input
                    type="checkbox"
                    checked={selectedIds.has(record.id)}
                    onChange={() => toggleSelect(record.id)}
                    disabled={!connected || !isIssuable(record)}
                    className="w-4 h-4"
                  />,
                  <span
                    onClick={() => setDetailRecord(record)}
                    className="text-[#4285F4] hover:underline cursor-pointer"
                  >
                    {record.orderNumber || "-"}
                  </span>,
                  <>{record.purchaserName || "-"}</>,
                  <span className="truncate block max-w-[180px]" title={record.orderName}>
                    {record.orderName}
                  </span>,
                  <>{record.orderTotalAmount?.toLocaleString() || "0"}원</>,
                  <Badge tone={record.invoiceType === "TAX_INVOICE" ? "blue" : "gray"}>
                    {INVOICE_TYPE_LABELS[record.invoiceType]}
                  </Badge>,
                  <span className="truncate block max-w-[150px]" title={buyerSummary(record)}>
                    {buyerSummary(record)}
                  </span>,
                  <span title={record.errorMessage ?? undefined}>
                    <Badge tone={record.status === "SUCCESS" ? "blue" : "gray"}>
                      {INVOICE_STATUS_LABELS[record.status]}
                    </Badge>
                    {record.pendingAdjustmentCount > 0 && (
                      <span
                        onClick={() => setDetailRecord(record)}
                        className="ml-1 cursor-pointer"
                        title="환불로 처리할 조정 건이 있습니다"
                      >
                        <Badge tone="blue">조정 {record.pendingAdjustmentCount}</Badge>
                      </span>
                    )}
                    {record.orderCanceledAfterIssue && (
                      <span
                        onClick={() => setDetailRecord(record)}
                        className="block mt-0.5 cursor-pointer"
                        title="발급 후 주문이 취소됐습니다. 상세에서 안내를 확인하세요."
                      >
                        <Badge tone="blue">주문 취소됨</Badge>
                      </span>
                    )}
                    {record.status === "SUCCESS" && !record.issuedViaIntegration && (
                      <span className="block text-[10px] text-[#888] mt-0.5">수기</span>
                    )}
                  </span>,
                  <span className="flex flex-col">
                    <span>{record.ntsState ? NTS_STATE_LABELS[record.ntsState] : "-"}</span>
                    {record.approvalNo && <span className="text-[10px] text-[#888]">{record.approvalNo}</span>}
                    {record.issuedAt && record.status !== "PENDING" && (
                      <span className="text-[10px] text-[#888]">{formatDate(record.issuedAt)}</span>
                    )}
                  </span>,
                  <span className="flex items-center gap-2">
                    {connected && isIssuable(record) && (
                      <button
                        onClick={() => handleRowIssue(record)}
                        disabled={rowBusyId === record.id}
                        className="px-2 py-0.5 text-[11px] rounded-[4px] bg-[#222] text-white hover:bg-[#333] disabled:bg-[#ccc]"
                      >
                        {rowBusyId === record.id ? "발급 중" : "발급"}
                      </button>
                    )}
                    {!record.orderPaid && record.status === "PENDING" && (
                      <span className="text-[11px] text-[#888]">결제 전</span>
                    )}
                    {record.issuedViaIntegration && record.status === "SUCCESS" && (
                      <button
                        onClick={() => handleRowView(record)}
                        className="text-[11px] text-[#4285F4] hover:text-[#3367D6]"
                      >
                        보기
                      </button>
                    )}
                    <button
                      onClick={() => setDetailRecord(record)}
                      className="text-[11px] text-[#888] hover:text-[#555]"
                    >
                      상세
                    </button>
                  </span>,
                ]),
              }}
            />
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-5 flex items-center justify-center gap-2 text-[13px]">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="px-3 h-[32px] rounded-[6px] border border-[#e0e0e0] disabled:text-[#bbb]"
            >
              이전
            </button>
            <span className="text-[#555]">
              {page + 1} / {totalPages}
            </span>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 h-[32px] rounded-[6px] border border-[#e0e0e0] disabled:text-[#bbb]"
            >
              다음
            </button>
          </div>
        )}
      </div>

      {detailRecord && (
        <InvoiceDetailDialog
          companyId={cid}
          invoice={detailRecord}
          connected={connected}
          onClose={() => setDetailRecord(null)}
          onChanged={replaceRecord}
        />
      )}

      {creating && (
        <InvoiceCreateDialog
          companyId={cid}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            loadRecords();
            setDetailRecord(created);
          }}
        />
      )}
    </CompanyAdminLayout>
  );
};

export default TaxReceipt;
